<template>
  <div class="trial-status-wrapper">
    <div
      class="wrapper"
      :style="{ backgroundColor: trialStatusData.backgroundColor }"
      v-if="showPanel">
      <div>
        <i class="fa-solid fa-briefcase"></i>
      </div>
      <div class="description">
        <h5>
          Pro trial
          <i class="fa-solid fa-circle"></i>
          {{ trialStatusData.statusMessage }}
        </h5>
        <router-link :to="trialStatusData.link">{{ trialStatusData.linkLabel }}</router-link>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useSubscriptionsStore } from '@/stores/subscriptions'

const subscriptionStore = useSubscriptionsStore()

interface TrialStatus {
  statusMessage: string
  linkLabel: string
  link: string
  backgroundColor: string
}

const showPanel = computed(() => {
  return (
    subscriptionStore.showV2TrialMessaging &&
    subscriptionStore.trialDaysLeft &&
    subscriptionStore.trialDaysLeft >= -7
  )
})

const trialStatusData = computed<TrialStatus>(() => {
  if (!subscriptionStore.showV2TrialMessaging || !subscriptionStore.trialEndDate) {
    return {
      statusMessage: '',
      linkLabel: '',
      link: '',
      backgroundColor: ''
    }
  }

  return setTrialStatus(subscriptionStore.trialDaysLeft)
})

const setTrialStatus = (daysLeft: number) => {
  const trialStatusConfig: Record<string, TrialStatus> = {
    started: {
      statusMessage: `${daysLeft} days left`,
      linkLabel: 'Explore trial & discounts',
      link: '/free-trial',
      backgroundColor: 'var(--color-surface-highlight)'
    },
    expiringSoon: {
      statusMessage: `${daysLeft} days left`,
      linkLabel: 'See upgrade options',
      link: '/organization/plans',
      backgroundColor: 'var(--color-surface-warning)'
    },
    expiresToday: {
      statusMessage: 'Ends today',
      linkLabel: 'Upgrade now',
      link: '/organization/plans',
      backgroundColor: 'var(--color-surface-critical)'
    },
    expired: {
      statusMessage: 'Ended',
      linkLabel: 'Upgrade now',
      link: '/organization/plans',
      backgroundColor: 'var(--color-surface-critical)'
    }
  }

  let key: keyof typeof trialStatusConfig | '' = ''
  if (daysLeft > 7) key = 'started'
  else if (daysLeft > 1) key = 'expiringSoon'
  else if (daysLeft === 0) key = 'expiresToday'
  else key = 'expired'
  return trialStatusConfig[key]
}
</script>

<style scoped>
.wrapper {
  width: 188px;
  border-radius: 3px;
  padding: 4px 8px;
  display: flex;
  gap: 8px;
}

.description {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

h5 {
  font-weight: 700;
  display: flex;
  align-items: center;
  margin: 0px;
}

h5 > i {
  margin: 0 5px;
  font-size: 3px;
}

a {
  cursor: pointer;
  font-size: 12px;
  line-height: 14px;
  color: var(--color-interactive);
}

a:hover {
  color: var(--color-interactive-hover);
  text-decoration: underline;
}

a:active {
  color: var(--color-interactive-pressed);
}
</style>
